import * as PIXI from "pixi.js";
import { symbolData, COIN_SYMBOL_IDS, BLANK_SYMBOL_ID, isCoinSymbol } from "../../SymbolData";
import { Settings } from "../../settings";
import { FEATURES_CONFIG } from "../../featuresConfig";
import { formatToCustomLocale } from "../../utilities";
import store from "../../State/Store/store";
import { FONT_STYLES, scaleStyle } from "../../fontStyles";

/**
 * PotCollectionSymbolController
 *
 * Per-cell spin engine for the Pot Collection feature.
 * Direct port of RoyalCollectSymbolController (which is a 1:1 port of
 * ReelControllerV2 for N=1 visible symbol).
 *
 * ReelControllerV2 formula (N = visible symbol count):
 *   SPRITE_COUNT = N*2 + 2
 *   VISIBLE_SYMBOL_INDEX = 1 + N
 *   STOP_MOVE_DISTANCE = N
 *
 * For N=1: 4 sprites, visible at index 2, stop moves by 1 symbol height.
 * [0: top buffer, 1: landing zone, 2: VISIBLE, 3: bottom buffer]
 *
 * Simplified vs RoyalCollectSymbolController:
 * - No blur system (PC has no blurred spritesheet)
 * - No collectedBlank logic (blanks are visible static textures in PC)
 * - No isPermanentRoyal (all blank cells are spinnable)
 * - No blurred coin value text
 */

// Dynamic sprite count formula: 1 top buffer + N visible + N landing zone + 1 bottom buffer
const SPRITE_COUNT = 4; // 1*2 + 2
const VISIBLE_SYMBOL_INDEX = 2; // 1 + 1
// Stop animation moves sprites down by the landing zone distance (N symbol heights)
const STOP_MOVE_DISTANCE = 1;

// Note: No COIN_VALUE_Y_OFFSET — text is centered at y=0 (matching overlay spine positioning)

// ── Symbol Pools (built dynamically from SymbolData) ────────────────────────
// PC spin shows ONLY: coins, jackpots, zeus orbs, and blanks.
// No base game symbols (0-9), no scatter, no variants without textures.

// Helper: filter symbolData entries by symbolPay type, requiring a valid texture
const _poolByPay = (payType) =>
  Object.entries(symbolData)
    .filter(([, d]) => d.symbolPay === payType && d.texture != null)
    .map(([id]) => parseInt(id, 10));

const PC_COIN_IDS = _poolByPay("coin");         // [15]  c1
// Jackpots (17-19, 21-23) are NOT in the spin pool — per GDD they only appear
// via Mystery Zeus reveals. Players should never see them spinning on the reels.
const PC_BLANK_IDS = _poolByPay("blank");        // [16]  bl

// Zeus orb IDs per feature — only active features appear in the spin pool.
// Mapping mirrors PotCollectionOrbActivator's FEATURE_TO_ZEUS_IDS (primary IDs only).
const FEATURE_TO_ZEUS_ID = {
  D: 12, // cd — Double
  M: 13, // cm — Mystery
  C: 14, // cc — Collect
};

// Probabilities — no jackpots in pool (they come from Mystery reveals only)
const PC_COIN_PROBABILITY = 0.30;
const PC_ZEUS_ORB_PROBABILITY = 0.10;
// Remaining (~60%) → blank

// Coin multiplier values from GDD — randomly picked for spinning pool display
const PC_SPIN_COIN_VALUES = [0.6, 1, 1.6, 2, 3, 5, 8, 10, 12];

class PotCollectionSymbolController {
  constructor(
    spinningContainer,
    symbolId,
    symbolWidth,
    symbolHeight,
    symbolIndex,
    reelIndex,
    onStopCallback = null,
    symbolsSpriteSheet = null,
  ) {
    this.spinningContainer = spinningContainer;
    this.symbolId = symbolId;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolIndex = symbolIndex;
    this.reelIndex = reelIndex;
    this.onStopCallback = onStopCallback;
    this.onLandingPeak = null; // Callback at overshoot trough (before full settle)
    this.spritesheet = symbolsSpriteSheet;

    // Animation state
    this.isPlaying = false;
    this.hasStopped = false;
    this.isStopping = false;
    this.isWindingUp = false;
    this.tickerCallback = null;
    this._windUpTickerFn = null;
    this.spinningSprites = [];
    // Truthy while a spin/stop cycle is active (timing uses _spinElapsed, not wall clock)
    this.spinStartTime = null;
    this._spinElapsed = 0; // Accumulated spin time (ms) — advances only when ticker runs
    this.normalizedSpinSpeed = 0;
    this.lastCalculatedPosition = 0;
    this.stopTweenActive = false;

    // Premium feel: acceleration
    this.accelerationMultiplier = 1.0;
    this.currentVelocity = 0;

    // Landing data from API
    this.pendingLandingSymbolId = null;

    // Ticker-based stop params (replaces anime.js / performance.now)
    this._stopElapsed = 0; // Accumulated stop animation time (ms)
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this._landingPeakFired = false;

    // Speed mode — set per-spin by the engine before calling startSpin()
    this.autoPlaySpeed = "auto";
    this._speedConfigCache = null;

    // Cached zeus-orb pool for _generateRandomSymbolId, keyed by respinGameType
    // (constant for the whole bonus) — avoids rebuilding the array on every
    // sprite wrap in the per-frame spin loop.
    this._zeusPoolGameType = null;
    this._zeusPoolIds = [];

    // Tension deceleration state (ported from ReelControllerV2)
    this.isTensionMode = false;
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0; // Accumulated tension deceleration time (ms)
    this._tensionDecelerationDuration = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Deferred resize state — ported from ReelControllerV2.
    // When spinning/stopping/winding-up, resize is deferred to safe points
    // (wrap boundary, finalize landing, next spin start) to prevent
    // corrupted stop easing distances and sprite position desync.
    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = symbolHeight;
    this._targetSymbolHeight = symbolHeight;
    this._targetSymbolWidth = symbolWidth;

    // Caches
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache = new Map();

    this._createSpinningSprites();
    this.spinningContainer.roundPixels = true;
    // Start hidden — spinningContainer.alpha controls visibility, not individual sprite.visible.
    // This prevents random textures from leaking through transparent symbols (e.g. blanks).
    this.spinningContainer.alpha = 0;
  }

  // ── Speed Config ───────────────────────────────────────────────────────

  /**
   * Returns the speed config for bonus spins.
   * Bonus always runs at AUTO speed — reads from FEATURES_CONFIG.POT_COLLECTION.SPIN_SPEED.
   */
  _getSpeedConfig() {
    if (this._speedConfigCache) {
      return this._speedConfigCache;
    }

    const config = FEATURES_CONFIG.POT_COLLECTION.SPIN_SPEED;
    this._speedConfigCache = config;

    return config;
  }

  // ── Sprite Creation (from ReelControllerV2) ────────────────────────────

  _createSpinningSprites() {
    if (!this.spinningContainer) return;

    for (let i = 0; i < SPRITE_COUNT; i++) {
      const symbolContainer = new PIXI.Container();
      symbolContainer.y = i * this.symbolHeight;
      symbolContainer.roundPixels = true;

      const normalSprite = new PIXI.Sprite();
      normalSprite.anchor.set(0.5);
      normalSprite.name = "spinning_symbol_texture";
      normalSprite.alpha = 1;
      normalSprite.roundPixels = true;
      symbolContainer.addChild(normalSprite);

      let valueText = null;
      if (COIN_SYMBOL_IDS.length > 0) {
        // Match overlay spine text size: the overlay creates text at base size and
        // divides by the bone scale (coin_main scaleX=0.94) to compensate for
        // spine applying that scale. Static text renders directly in PIXI with
        // no bone transform, so it should just use the base font size — no
        // bone scale multiplication needed.
        const fontSize =
          this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        const textStyle = new PIXI.TextStyle(scaleStyle(FONT_STYLES.coinValue, fontSize));
        valueText = new PIXI.Text("", textStyle);
        valueText.anchor.set(0.5);
        valueText.name = "symbol_value";
        valueText.zIndex = 1;
        valueText.alpha = 0;
        valueText.roundPixels = true;
        symbolContainer.addChild(valueText);
      }

      this.spinningSprites.push({
        container: symbolContainer,
        normalSprite,
        valueText,
        currentSymbolId: null,
      });

      this.spinningContainer.addChild(symbolContainer);
    }

    // Initialize with random textures
    this._initializeSpinningTextures();

    // Sprites are always visible — the parent container's mask
    // (set in PotCollectionSymbol.jsx) clips to 1 symbolHeight,
    // so only the sprite at the visible position shows through.
  }

  _initializeSpinningTextures() {
    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === VISIBLE_SYMBOL_INDEX) {
        // Visible sprite matches the cell's actual symbol so it doesn't
        // leak a random texture through transparent symbols (e.g. blank).
        this._setSpriteTexture(this.spinningSprites[i], this.symbolId);
      } else {
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[i], randomId);
      }
    }
  }

  // ── Random Symbol Generation ──────────────────────────────────────────

  // Generates a random symbol from the PC pool with probability-based distribution.
  // Only coins, active zeus orbs (based on respinGameType), and blanks — no jackpots.
  _generateRandomSymbolId() {
    const roll = Math.random();

    // Build zeus orb pool dynamically from active features.
    // Cached by respinGameType (constant for the whole bonus) so the array is
    // only rebuilt when the type actually changes — this method runs per wrapped
    // sprite per frame during spins. Same pool contents as before.
    const respinGameType = store.getState().potCollection?.respinGameType || "";
    if (this._zeusPoolGameType !== respinGameType) {
      this._zeusPoolGameType = respinGameType;
      const ids = [];
      for (const [feature, id] of Object.entries(FEATURE_TO_ZEUS_ID)) {
        if (respinGameType.includes(feature)) {
          ids.push(id);
        }
      }
      this._zeusPoolIds = ids;
    }
    const activeZeusIds = this._zeusPoolIds;

    // Zeus orb symbols — rarest (only active features)
    if (activeZeusIds.length > 0 && roll < PC_ZEUS_ORB_PROBABILITY) {
      return activeZeusIds[Math.floor(Math.random() * activeZeusIds.length)];
    }

    // Coin symbols
    if (PC_COIN_IDS.length > 0 && roll < PC_ZEUS_ORB_PROBABILITY + PC_COIN_PROBABILITY) {
      return PC_COIN_IDS[Math.floor(Math.random() * PC_COIN_IDS.length)];
    }

    // Default: blank
    if (PC_BLANK_IDS.length > 0) {
      return PC_BLANK_IDS[Math.floor(Math.random() * PC_BLANK_IDS.length)];
    }

    return BLANK_SYMBOL_ID;
  }

  // ── Coin Display Value (from ReelControllerV2) ─────────────────────────

  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseFloat(symbolValue);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      // Values are already in subunits (pre-calculated by server or
      // normalized by RGS adapter) — just format, no bet multiplication
      return formatToCustomLocale(coinValue);
    } catch (error) {
      return "";
    }
  }

  // ── Symbol Scale (from ReelControllerV2) ───────────────────────────────

  // Calculates uniform symbol scale based on standard symbol reference height.
  // All symbols get the SAME scale factor — premium symbols with larger sourceSize
  // naturally overflow their cells, preserving the intended size relationships.
  // Cached per container dimensions for performance (scale is symbol-independent).
  _calculateSymbolScale(containerWidth, containerHeight, symbolId) {
    if (!containerHeight) return 1;

    // Look up symbolPay for padding skip
    const info = symbolId != null ? symbolData[symbolId] : null;
    const symbolPay = info?.symbolPay || '';

    // Check cache — key includes symbolPay since feature symbols skip padding
    const cacheKey = `uniform_${containerWidth}_${containerHeight}_${symbolPay}`;
    if (this._nativeDimensionsCache.has(cacheKey)) {
      return this._nativeDimensionsCache.get(cacheKey);
    }

    // No padding in Pot Collection — all symbols render at natural size
    const padding = 0;
    const scale = (containerHeight / Settings.SYMBOL_HEIGHT) * (1 - padding * 2);

    // Cache result
    this._nativeDimensionsCache.set(cacheKey, scale);

    return scale;
  }

  // ── Texture Management (from ReelControllerV2) ─────────────────────────

  // Sets sprite texture and coin value text
  _setSpriteTexture(spriteData, symbolId) {
    // Skip cache for coins — value randomizes each time even though symbolId is the same
    if (spriteData.currentSymbolId === symbolId && !isCoinSymbol(symbolId)) {
      return;
    }

    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    spriteData.currentSymbolId = symbolId;
    const textureName = symbolInfo.texture;
    // For coins: use actual API value for landing sprite, random for buffer sprites
    let symbolValue = symbolInfo.value;
    if (isCoinSymbol(symbolId)) {
      if (spriteData._isLandingSprite) {
        // Read from valuesGrid (storage.values) — same source the overlay uses
        const pcState = store.getState().potCollection;
        const values = pcState?.valuesGrid;
        let apiValue = values?.[this.reelIndex]?.[this.symbolIndex];

        // BE sends post-double values — divide to get pre-double display value
        if (apiValue && apiValue > 0) {
          const screenProgress = pcState?.screenProgress;
          if (screenProgress) {
            let doublesCount = 0;
            for (const action of screenProgress) {
              if (action.action !== 'd_doubles') continue;
              const coords = action.coordinates;
              if (!coords) continue;
              for (const coord of coords) {
                if (Array.isArray(coord) && coord[0] === this.reelIndex && coord[1] === this.symbolIndex) {
                  doublesCount++;
                  break;
                }
              }
            }
            if (doublesCount > 0) {
              apiValue = Math.round(apiValue / Math.pow(2, doublesCount));
            }
          }
        }

        symbolValue = (apiValue && apiValue > 0) ? apiValue : symbolInfo.value;
      } else {
        // Random spinning values are multipliers — convert to subunits (* bet)
        // so _getCoinDisplayValue can format them consistently with API values
        const bet = store.getState()?.game?.bet || 1;
        symbolValue = PC_SPIN_COIN_VALUES[Math.floor(Math.random() * PC_SPIN_COIN_VALUES.length)] * bet;
      }
    }

    // If texture is null or empty, set empty textures and skip processing
    if (!textureName || textureName === null) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    if (this.spritesheet && textureName) {
      const texture =
        this.spritesheet.textures?.[`${textureName}.png`] ||
        PIXI.Texture.EMPTY;
      spriteData.normalSprite.texture = texture;
      spriteData.normalSprite.alpha = 1;
    }

    const scale = this._calculateSymbolScale(
      this.symbolWidth,
      this.symbolHeight,
      symbolId,
    );
    if (spriteData.normalSprite) {
      spriteData.normalSprite.scale.set(scale);
    }

    // Update coin value text — ONLY for coin symbols (not jackpots which display tier via spine)
    if (spriteData.valueText && symbolValue && isCoinSymbol(symbolId)) {
      const displayValue = this._getCoinDisplayValue(symbolValue);
      spriteData.valueText.text = displayValue;
      spriteData.valueText.alpha = displayValue !== "" ? 1 : 0;
      // Centered on coin — sprite is anchored at 0.5 so y=0 = center.
      // (Overlay positions text via spine's "value" bone which is also centered.)
      spriteData.valueText.y = 0;

      // Apply scaling logic if text has content
      if (displayValue !== "") {
        // Reset scale first
        spriteData.valueText.scale.set(1);

        // Force update to get accurate measurements
        spriteData.valueText.updateText();

        // Calculate available width (with padding matching overlay: 5% on each side)
        const coinWidth = this.symbolWidth;
        const paddingPercent = 0.05;
        const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

        const actualTextWidth = spriteData.valueText.width;

        // Progressive width compression (matching overlay pattern):
        // ≤8 digits: squish X only (preserve height)
        // 9+ digits: uniform scale
        if (actualTextWidth > maxTextWidth) {
          const digitCount = displayValue.replace(/[^0-9]/g, '').length;
          const textScale = maxTextWidth / actualTextWidth;
          if (digitCount <= 8) {
            spriteData.valueText.scale.x = textScale;
          } else {
            spriteData.valueText.scale.set(textScale);
          }
        }
      }
    } else if (spriteData.valueText) {
      // Non-coin symbol: clear text without heavy processing
      spriteData.valueText.text = "";
      spriteData.valueText.alpha = 0;
    }
  }

  // ── Start Spin ────────────────────────────────────────────────────────

  startSpin() {
    if (this.isPlaying) {
      return;
    }

    // Apply any deferred resize before starting the spin
    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    this.isPlaying = true;
    this.hasStopped = false;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.accelerationMultiplier = 0; // Ramp from 0
    this.currentVelocity = 0;
    // pendingLandingSymbolId is cleared by the engine before startSpin() is
    // called. New API data arrives via setLandingSymbolId() after rgsPlay().

    // Reset stop params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;

    // Reset tension state
    this._resetTensionState();

    // Calculate spin speed from speed-dependent config
    const speedConfig = this._getSpeedConfig();
    this.normalizedSpinSpeed =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    this.spinStartTime = true; // sentinel — timing uses _spinElapsed
    this._spinElapsed = 0;
    this.lastCalculatedPosition = 0;

    // Reset sprite positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Sprites are always visible — mask handles clipping
    // Show the spinning container (hidden at rest to prevent texture leak-through)
    this.spinningContainer.alpha = 1;

    // Set visible sprite (index 2) to current symbol so it visually pushes down,
    // other sprites get random textures for the spinning animation
    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === VISIBLE_SYMBOL_INDEX) {
        this._setSpriteTexture(this.spinningSprites[i], this.symbolId);
      } else {
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[i], randomId);
      }
    }

    // Start ticker loop
    this.tickerCallback = () => {
      if (this.spinStartTime === null || this.hasStopped) return;
      this.updateSymbolsAbsolute();
    };
    PIXI.Ticker.shared.add(this.tickerCallback);
  }

  // ── Start Spin No Wind-Up (TURBO — instant full speed) ────────────

  /**
   * Start spinning at full speed with no acceleration ramp.
   * Used by the turbo wait-for-server drop pattern:
   *   API responds → startSpinNoWindUp() → immediately stopSpin()
   * Symbols only travel the configured stop distance before landing.
   */
  startSpinNoWindUp() {
    if (this.isPlaying) return;

    // Apply any deferred resize before starting the spin
    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    this.isPlaying = true;
    this.hasStopped = false;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.accelerationMultiplier = 1.0; // Full speed immediately — no ramp
    this.currentVelocity = 1.0;

    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;

    const speedConfig = this._getSpeedConfig();
    this.normalizedSpinSpeed =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    this.spinStartTime = true; // sentinel — timing uses _spinElapsed
    this._spinElapsed = 0;
    this.lastCalculatedPosition = 0;

    // Reset sprite positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    this.spinningContainer.alpha = 1;

    // Set visible sprite to current symbol, others get random textures
    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === VISIBLE_SYMBOL_INDEX) {
        this._setSpriteTexture(this.spinningSprites[i], this.symbolId);
      } else {
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[i], randomId);
      }
    }

    // Start ticker loop
    this.tickerCallback = () => {
      if (this.spinStartTime === null || this.hasStopped) return;
      this.updateSymbolsAbsolute();
    };
    PIXI.Ticker.shared.add(this.tickerCallback);
  }

  // ── Start Spin with Wind-Up (AUTO speed only) ──────────────────────

  /**
   * Start spin with a wind-up animation: symbol pulls upward, then releases
   * into the spin. Wind-up distance = landing bounce overshoot distance.
   *
   * Uses ticker-based sprite position offsets (not anime.js) to avoid
   * React re-render conflicts.
   *
   * @returns {Promise<void>} Resolves when wind-up completes and spin starts
   */
  startSpinWithWindUp() {
    if (this.isPlaying || this.isWindingUp) {
      return Promise.resolve();
    }

    this.isWindingUp = true;

    // Apply any deferred resize before starting the wind-up
    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    // ── Sprite setup (same as startSpin) ──────────────────────────────
    this.hasStopped = false;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.accelerationMultiplier = 0;
    this.currentVelocity = 0;
    // pendingLandingSymbolId is cleared by the engine before startSpinWithWindUp()
    // is called. New API data arrives via setLandingSymbolId() after rgsPlay().

    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;

    const speedConfig = this._getSpeedConfig();
    this.normalizedSpinSpeed =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;

    // Reset sprite positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Show the spinning container (hidden at rest to prevent texture leak-through)
    this.spinningContainer.alpha = 1;

    // Set visible sprite to current symbol, others get random textures
    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === VISIBLE_SYMBOL_INDEX) {
        this._setSpriteTexture(this.spinningSprites[i], this.symbolId);
      } else {
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[i], randomId);
      }
    }

    // ── Wind-up distance ──────────────────────────────────────────────
    // Simple fraction of symbolHeight — tunable via config (WIND_UP_DISTANCE)
    // Negative = pull upward
    const windUpDistance = -(speedConfig.WIND_UP_DISTANCE || 0.15) * this.symbolHeight;

    // ── Wind-up timing ───────────────────────────────────────────────
    // Derive from stop timing to match bounce feel (same as base game)
    const s = speedConfig.BOUNCE_OVERSHOOT;
    const spinVelocity =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    const totalStopDuration =
      (((s + 3) * STOP_MOVE_DISTANCE * this.symbolHeight * 1000) /
        spinVelocity) *
      (speedConfig.STOP_SLOWDOWN_FACTOR || FEATURES_CONFIG.POT_COLLECTION.STOP_SLOWDOWN_FACTOR || 1.0);
    const bounceFraction = (2 * s) / (3 * (s + 1));
    const windUpSpeedFactor = speedConfig.WIND_UP_SPEED_FACTOR ?? 1.0;
    const windUpPullDuration = Math.round(totalStopDuration * bounceFraction * windUpSpeedFactor) || 55;
    const windUpReleaseDuration = Math.round(windUpPullDuration * 0.6);

    // ── Ticker-based wind-up animation ───────────────────────────────
    return new Promise((resolve) => {
      let elapsed = 0;

      const windUpTicker = () => {
        elapsed += PIXI.Ticker.shared.deltaMS;

        let offset;
        if (elapsed < windUpPullDuration) {
          // Phase 1: Pull up (easeOutCubic — fast start, gentle peak)
          const t = Math.min(elapsed / windUpPullDuration, 1);
          const eased = 1 - (1 - t) * (1 - t) * (1 - t);
          offset = windUpDistance * eased;
        } else {
          // Phase 2: Release back to origin (easeInCubic — accelerates into spin)
          const releaseElapsed = elapsed - windUpPullDuration;
          const t = Math.min(releaseElapsed / windUpReleaseDuration, 1);
          const eased = t * t * t;
          offset = windUpDistance * (1 - eased);
        }

        // Apply offset to ALL sprite positions (not container.y)
        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y = i * this.symbolHeight + offset;
        }

        // Check completion
        if (elapsed >= windUpPullDuration + windUpReleaseDuration) {
          PIXI.Ticker.shared.remove(windUpTicker);
          this._windUpTickerFn = null;

          // Reset to exact grid positions
          for (let i = 0; i < this.spinningSprites.length; i++) {
            this.spinningSprites[i].container.y = i * this.symbolHeight;
          }

          this.isWindingUp = false;

          // Start actual spin (ticker loop) — same as end of startSpin()
          this.isPlaying = true;
          this.spinStartTime = true; // sentinel — timing uses _spinElapsed
          this._spinElapsed = 0;
          this.lastCalculatedPosition = 0;

          this.tickerCallback = () => {
            if (this.spinStartTime === null || this.hasStopped) return;
            this.updateSymbolsAbsolute();
          };
          PIXI.Ticker.shared.add(this.tickerCallback);

          resolve();

          // Notify engine immediately — no timeout/buffer needed
          if (this.onWindUpComplete) this.onWindUpComplete();
        }
      };

      this._windUpTickerFn = windUpTicker;
      PIXI.Ticker.shared.add(windUpTicker);
    });
  }

  // ── Stop Spin ─────────────────────────────────────────────────────────

  stopSpin(isManualStop = false) {
    if (this.isStopping || this.hasStopped) {
      return;
    }
    // Allow stop during spinning OR wind-up.
    // During wind-up: isStopping flag is set now, and when wind-up completes
    // and the spin ticker starts, it'll execute the stop on the first wrap.
    if (!this.isPlaying && !this.isWindingUp) {
      return;
    }
    this._isManualStop = isManualStop;
    this.isStopping = true;
  }

  /**
   * Turbo stop — bypass the wrap-wait and execute stop easing immediately.
   * Normal stop waits for a sprite to wrap before executing _calculateAndExecuteStop,
   * which means the symbol can travel an entire cycle. Turbo needs instant stop
   * from the current position with extra travel distance for visible drop.
   */
  stopSpinImmediate() {
    if (this.hasStopped || this.stopTweenActive) return;
    if (!this.isPlaying) return;

    this.isStopping = true;

    // Execute stop easing NOW — don't wait for wrap
    if (this.pendingLandingSymbolId !== null) {
      this._calculateAndExecuteStop();
    }
  }

  /**
   * Set the symbol ID that should appear when this symbol stops.
   * Called by the engine when API response arrives.
   * Do NOT paint textures here — only _setLandingTexture at stop start may
   * write the landing id (painting earlier scrolls into the visible strip).
   */
  setLandingSymbolId(symbolId) {
    this.pendingLandingSymbolId = symbolId;
  }

  // ── Main Animation Loop (from ReelControllerV2) ───────────────────────

  updateSymbolsAbsolute() {
    if (this.spinStartTime === null || this.hasStopped) return;

    // ── Frame-start: apply any deferred resize immediately ──
    // This ensures resize applies within 1 frame (~16ms) regardless of which
    // animation phase we're in (normal spin, stop tween, tension, etc.).
    // Previously this only fired at specific safe points (wrap boundary, stop
    // easing branch) which missed controllers in transitional states.
    if (this._pendingPositionUpdate) {

      // If stop easing is active, rescale stop distance proportionally
      // so the easing lands at the correct position at the new height.
      if (this.stopTweenActive && this._stopDuration > 0) {
        const scaleRatio = this._targetSymbolHeight / this.symbolHeight;
        if (Math.abs(scaleRatio - 1.0) > 0.0001) {
          this._stopDistance *= scaleRatio;
          this._stopLastEasedPos *= scaleRatio;
        }
      }
      this._applyPendingResize();
    }

    // ── Ticker-based stop animation (BackOut easing inside same ticker) ──
    if (this.stopTweenActive && this._stopDuration > 0) {
      this._stopElapsed += PIXI.Ticker.shared.deltaMS;
      const elapsed = this._stopElapsed;
      const t = Math.min(elapsed / this._stopDuration, 1.0);

      // BackOut easing: f(t) = (t-1)² × ((s+1)(t-1) + s) + 1
      const s = this._stopOvershoot;
      const t1 = t - 1;
      const easedT = t1 * t1 * ((s + 1) * t1 + s) + 1;
      const easedPosition = this._stopDistance * easedT;
      const delta = easedPosition - this._stopLastEasedPos;
      this._stopLastEasedPos = easedPosition;

      if (Math.abs(delta) > 0.0001) {
        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        // Wrap check during stop
        const wrapThreshold = this._getWrapThreshold();
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }
      }

      // Detect overshoot peak (trough) — lowest visual point before bounce-back.
      // Ported from ReelControllerV2: t_peak = 1 - 2s / (3(s+1))
      if (!this._landingPeakFired && this._stopOvershoot > 0) {
        const sPeak = this._stopOvershoot;
        const tPeakMath = 1 - (2 * sPeak) / (3 * (sPeak + 1));
        const peakOffset = this._getSpeedConfig()?.LANDING_PEAK_OFFSET ?? 1.0;
        const tPeak = tPeakMath * peakOffset;
        if (t >= tPeak) {
          this._landingPeakFired = true;
          if (this.onLandingPeak) {
            this.onLandingPeak(this.reelIndex, this.symbolIndex);
          }
        }
      }

      if (t >= 1.0) {
        this._finalizeLanding();
      }
      return;
    }

    // ── Normal spin loop — accumulate ticker time (freezes when tab hidden) ──
    this._spinElapsed += PIXI.Ticker.shared.deltaMS;
    const elapsedTime = this._spinElapsed / 1000;
    const currentExpectedPosition = elapsedTime * this.normalizedSpinSpeed;

    let movement = currentExpectedPosition - this.lastCalculatedPosition;
    this.lastCalculatedPosition = currentExpectedPosition; // Always track at full speed

    // --- Tension deceleration (smooth slowdown for anticipation) ---
    // Scales the per-frame visual movement while keeping position tracking at full speed.
    // Ported from ReelControllerV2 — same easing and math.
    if (this._tensionDecelerating) {
      const tensionDecel = FEATURES_CONFIG.TENSION.REEL_DECELERATION;
      this._tensionDecelElapsed += PIXI.Ticker.shared.deltaMS;
      const tensionElapsed = this._tensionDecelElapsed;
      const tDecel = Math.min(tensionElapsed / this._tensionDecelerationDuration, 1.0);
      // Ease-out cubic: starts slowing immediately, arrives at target speed gradually.
      const t1 = 1 - tDecel;
      const easedDecel = 1 - (t1 * t1 * t1);
      this._tensionSpeedMultiplier = 1.0 - (1.0 - tensionDecel.TARGET_SPEED) * easedDecel;

      // Scale visual movement only — position tracker stays at full speed
      movement *= this._tensionSpeedMultiplier;
    }

    // Acceleration ramp (ease-out cubic: fast start, smooth arrival at full speed)
    const speedConfig = this._getSpeedConfig();
    // Support ratio-based (ACCEL_SPEED_RATIO × SPEED_MS) or legacy absolute (ACCELERATION_DURATION)
    const accelDuration = speedConfig.ACCELERATION_DURATION
      ?? (speedConfig.ACCEL_SPEED_RATIO
        ? speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT * speedConfig.ACCEL_SPEED_RATIO
        : 0);
    if (accelDuration > 0 && this.accelerationMultiplier < 1.0) {
      const elapsedMs = this._spinElapsed;
      const accelT = Math.min(elapsedMs / accelDuration, 1.0);
      // Ease-out cubic: starts fast, smoothly reaches 1.0
      this.accelerationMultiplier =
        1 - (1 - accelT) * (1 - accelT) * (1 - accelT);
    } else {
      this.accelerationMultiplier = 1.0;
    }

    // Apply acceleration multiplier
    movement *= this.accelerationMultiplier;

    // ── Frame-spike movement cap ──
    // During resize, React re-renders the entire component tree in the same rAF
    // frame (730ms+). Without a cap, the position calc includes this dead time as
    // movement → sprites overshoot multiple wrap thresholds → infinite spin.
    // Cap at 0.95× symbolHeight — guarantees at most ONE wrap per frame.
    const maxMovement = this.symbolHeight * 0.95;
    if (movement > maxMovement) {
      movement = maxMovement;
    }

    // Track velocity for reference (no blur — just state tracking)
    this.currentVelocity = this.accelerationMultiplier;

    const wrapThreshold = this._getWrapThreshold();
    const shouldUpdateTexture = !this.isStopping && !this.hasStopped;

    let shouldExecuteStop = false;

    // Phase 1: Apply movement to ALL sprites uniformly (no break-and-snap)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y += movement;
    }

    // Phase 2: Check for wraps AFTER all sprites have moved
    // Use while loop (not if) to handle multiple wraps in case any edge case
    // gets past the movement cap (e.g. manual position manipulation, rounding).
    const lastIndex = this.spinningSprites.length - 1;
    while (this.spinningSprites[lastIndex].container.y >= wrapThreshold) {

      this._moveLastSymbolOnTop();

      if (shouldUpdateTexture) {
        // Update texture on the sprite that just wrapped to top (now index 0)
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[0], randomId);
      }

      if (this.isStopping && !this.hasStopped && !this.stopTweenActive) {
        // Only stop when we have landing data from the API
        // (prevents landing on a random symbol if stop is triggered before API response)
        if (this.pendingLandingSymbolId !== null) {
          shouldExecuteStop = true;
          break; // Stop found — exit wrap loop, execute stop below
        }
      }
    }

    if (shouldExecuteStop) {
      this._calculateAndExecuteStop();
    }
  }

  // ── Wrap (from ReelControllerV2) ──────────────────────────────────────

  // Wraps last sprite to top — single-sprite teleport (no grid-snap on other sprites)
  _moveLastSymbolOnTop() {
    const movedSymbol = this.spinningSprites.pop();
    this.spinningSprites.unshift(movedSymbol);

    // Teleport only the wrapped sprite to the top position.
    // Other sprites keep their exact Y positions — no grid-snap stutter.
    const totalStripHeight = SPRITE_COUNT * this.symbolHeight;
    movedSymbol.container.y -= totalStripHeight;
  }

  _getWrapThreshold() {
    if (
      this._wrapThresholdCache === null ||
      this._lastSymbolHeightForWrapThreshold !== this.symbolHeight
    ) {
      this._wrapThresholdCache = SPRITE_COUNT * this.symbolHeight;
      this._lastSymbolHeightForWrapThreshold = this.symbolHeight;
    }
    return this._wrapThresholdCache;
  }

  // ── Stop Calculation (from ReelControllerV2) ──────────────────────────

  _calculateAndExecuteStop() {
    if (this.hasStopped || this.stopTweenActive) {
      return;
    }
    this.stopTweenActive = true;
    this._landingPeakFired = false; // Reset for this landing

    // Set landing texture on the landing zone sprite
    this._setLandingTexture();

    // Calculate drift compensation
    const currentDrift = this._calculateCurrentDrift();
    const baseLandingDistance = STOP_MOVE_DISTANCE * this.symbolHeight;
    let adjustedLandingDistance = baseLandingDistance - currentDrift;

    const speedConfig = this._getSpeedConfig();
    // Manual slam: N=1 travel can collapse to ~0 after drift — floor distance so
    // the symbol always drops from above. No duration floor (that caused empty crawl).
    if (this._isManualStop) {
      const minFrac = speedConfig.MIN_STOP_DISTANCE_FRACTION_MANUAL ?? 0.75;
      const minDistance = this.symbolHeight * minFrac;
      if (adjustedLandingDistance < minDistance) {

        adjustedLandingDistance = minDistance;
      }
    }

    // Manual stop: use snappier overshoot for more responsive feel
    const overshoot = (this._isManualStop && speedConfig.BOUNCE_OVERSHOOT_MANUAL != null)
      ? speedConfig.BOUNCE_OVERSHOOT_MANUAL
      : speedConfig.BOUNCE_OVERSHOOT;

    // Calculate stop duration — use LANDING_DURATION (fixed) when available,
    // otherwise fall back to velocity-matched calc
    let stopDuration;
    const landingDuration = speedConfig.LANDING_DURATION || null;
    if (landingDuration) {
      stopDuration = landingDuration;
    } else {
      // Velocity-matched: landing adapts to current spin speed (including tension deceleration)
      // Matches ReelControllerV2 — when tension is active, _tensionSpeedMultiplier reduces
      // effectiveVelocity, which naturally produces a longer, gentler landing.
      const effectiveVelocity = this.normalizedSpinSpeed
        * (this._tensionDecelerating ? this._tensionSpeedMultiplier : 1.0);
      const velocityMatchedDuration =
        ((overshoot + 3) * adjustedLandingDistance * 1000) / effectiveVelocity;
      const slowdownFactor = (this._isManualStop && speedConfig.STOP_SLOWDOWN_FACTOR_MANUAL != null)
        ? speedConfig.STOP_SLOWDOWN_FACTOR_MANUAL
        : (speedConfig.STOP_SLOWDOWN_FACTOR || FEATURES_CONFIG.POT_COLLECTION.STOP_SLOWDOWN_FACTOR || 1.0);
      stopDuration = velocityMatchedDuration * slowdownFactor;

      // Tension landing: stretch duration to compensate for overshoot appearing faster against slow spin.
      // Per-speed override (speedConfig.TENSION_LANDING_SLOWDOWN) takes priority over global config.
      if (this._tensionDecelerating) {
        const tensionLandingSlowdown = speedConfig.TENSION_LANDING_SLOWDOWN
          ?? FEATURES_CONFIG.TENSION?.REEL_DECELERATION?.TENSION_LANDING_SLOWDOWN
          ?? 1.0;
        stopDuration *= tensionLandingSlowdown;
      }
    }

    // Store stop params for the ticker (elapsed accumulates via deltaMS)
    this._stopElapsed = 0;
    this._stopDistance = adjustedLandingDistance;
    this._stopDuration = stopDuration;
    this._stopOvershoot = overshoot;
    this._stopLastEasedPos = 0;

    this.isPlaying = false;
  }

  _setLandingTexture() {
    const landingId =
      this.pendingLandingSymbolId !== null
        ? this.pendingLandingSymbolId
        : this._generateRandomSymbolId();

    // Flag if a jackpot ID leaked through
    if (landingId === 17 || landingId === 18 || landingId === 19 ||
        landingId === 21 || landingId === 22 || landingId === 23) {

    }

    // Set on sprite[1] only (the landing zone, above visible) — only at stop start
    // Mark as landing sprite so _setSpriteTexture uses the real API value
    if (this.spinningSprites.length >= 2) {
      this.spinningSprites[1]._isLandingSprite = true;
      this._setSpriteTexture(this.spinningSprites[1], landingId);
      this.spinningSprites[1]._isLandingSprite = false;
    }

    // Track the symbol that will land
    this.symbolId = landingId;
  }

  _calculateCurrentDrift() {
    if (!this.spinningSprites.length) return 0;
    const expectedY = VISIBLE_SYMBOL_INDEX * this.symbolHeight;
    const actualY = this.spinningSprites[VISIBLE_SYMBOL_INDEX].container.y;
    return actualY - expectedY;
  }

  // ── Finalize Landing (from ReelControllerV2) ──────────────────────────

  _finalizeLanding() {

    // Remove ticker
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Clear stop params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;

    // Reset tension deceleration state (matches ReelControllerV2._finalizeLanding)
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Apply deferred resize before snapping to grid (safe point)
    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    // Rotate array: move last STOP_MOVE_DISTANCE sprites to front (single O(n) operation)
    const cut = this.spinningSprites.length - STOP_MOVE_DISTANCE;
    const tail = this.spinningSprites.splice(cut);
    this.spinningSprites.unshift(...tail);

    // Reset positions to grid
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Set critical state flags immediately
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.currentVelocity = 0;
    this.spinStartTime = null;
    this._spinElapsed = 0;

    // Hide spinning container — the static sprite + overlay spine take over.
    // Using container alpha (not individual sprite.visible) prevents
    // random textures from leaking through transparent symbols.
    this.spinningContainer.alpha = 0;

    // Fire stop callback to the spin engine
    if (this.onStopCallback) {
      this.onStopCallback(this.symbolIndex, this.reelIndex);
    }
  }

  // ── Resize (deferred during animation — ported from ReelControllerV2) ────
  //
  // When spinning, stopping, or winding up, resize is DEFERRED to safe points
  // (wrap boundary, stop easing rescale, finalize landing, next spin start).
  // This prevents corrupted stop easing distances and sprite position desync
  // that cause the spin to get lost on orientation flip / window resize.
  //
  // When idle (not animating), resize is applied immediately.

  resize(symbolWidth, symbolHeight) {
    // No change — skip
    if (this.symbolHeight === symbolHeight && this.symbolWidth === symbolWidth) {
      this._pendingPositionUpdate = false;
      return;
    }

    this._targetSymbolHeight = symbolHeight;
    this._targetSymbolWidth = symbolWidth;

    // If animating: defer update to avoid mid-animation position chaos
    // isStopping covers the gap between stopSpin() and _calculateAndExecuteStop()
    const isAnimating = (this.isPlaying || this.isStopping || this.stopTweenActive || this.isWindingUp) && !this.hasStopped;
    if (isAnimating) {
      this._originalSymbolHeight = this.symbolHeight;
      this._pendingPositionUpdate = true;
      return;
    }

    // Not animating: update immediately
    this._originalSymbolHeight = this.symbolHeight;
    this._pendingPositionUpdate = false;

    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    // Invalidate caches
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache.clear();
    this._speedConfigCache = null;

    // Reset to grid positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Rescale all sprite textures + text
    this._rescaleAllTextures();
  }

  /**
   * Apply a deferred resize — called at safe points during animation.
   * Proportionally scales sprite positions and recalculates speed so the
   * animation continues smoothly at the new dimensions.
   * Ported from ReelControllerV2._applyPendingResize().
   */
  _applyPendingResize() {
    if (!this._pendingPositionUpdate) return;

    const oldSymbolHeight = this._originalSymbolHeight || this.symbolHeight;
    const newSymbolHeight = this._targetSymbolHeight;
    const scaleRatio = newSymbolHeight / oldSymbolHeight;
    const isSpinning = this.spinStartTime && !this.hasStopped;

    // Update dimensions
    this.symbolHeight = this._targetSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;

    // Invalidate caches
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache.clear();
    this._speedConfigCache = null;

    if (isSpinning && Math.abs(scaleRatio - 1.0) > 0.0001) {
      // ── Proportional mid-spin scaling ──
      // Scale sprite positions to match new symbolHeight — prevents discontinuous jump.
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y =
          Math.round(this.spinningSprites[i].container.y * scaleRatio * 100000) / 100000;
      }

      // Scale lastCalculatedPosition — critical to prevent movement jump next frame
      this.lastCalculatedPosition =
        Math.round(this.lastCalculatedPosition * scaleRatio * 100000) / 100000;

      // Recalculate spin speed with new symbolHeight
      const speedConfig = this._getSpeedConfig();
      this.normalizedSpinSpeed =
        (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    } else if (!isSpinning) {
      // Not spinning — reset to grid positions
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
    }

    // Rescale all sprite textures + text
    this._rescaleAllTextures();

    // Update spinning container offset to match new symbolHeight
    // (VISIBLE_SYMBOL_INDEX = 2, so offset = -2 * symbolHeight)
    if (this.spinningContainer) {
      this.spinningContainer.y = -2 * this.symbolHeight;
    }

    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = this.symbolHeight;
  }

  /**
   * Rescale all spinning sprite textures + coin value text to current dimensions.
   * Extracted to avoid duplication between resize() and _applyPendingResize().
   */
  _rescaleAllTextures() {
    const newFontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];

      if (spriteData.valueText) {
        Object.assign(spriteData.valueText.style, scaleStyle(FONT_STYLES.coinValue, newFontSize));
      }

      if (spriteData.currentSymbolId !== null) {
        const currentId = spriteData.currentSymbolId;
        spriteData.currentSymbolId = null;
        this._setSpriteTexture(spriteData, currentId);
      }
    }
  }

  // ── Tension Deceleration (ported from ReelControllerV2) ───────────────

  /**
   * Enter tension mode: gradually decelerate from full speed to TARGET_SPEED.
   * The deceleration is applied as a speed multiplier in updateSymbolsAbsolute().
   * Ported directly from ReelControllerV2.spinTension().
   */
  spinTension() {
    if (!this.isPlaying || this.isStopping || this.hasStopped) return;

    const stConfig = FEATURES_CONFIG.POT_COLLECTION.SYMBOL_TENSION;
    if (!stConfig?.ENABLED) return;

    this.isTensionMode = true;

    const tensionDecel = FEATURES_CONFIG.TENSION?.REEL_DECELERATION;
    if (tensionDecel?.ENABLED) {
      // Use symbol tension duration from config (matches the SpinEngine's scheduled stop timer)
      const cycleDuration = stConfig.DURATION ?? 3500;
      this._tensionDecelerationDuration = cycleDuration * (tensionDecel.DECELERATION_RATIO ?? 1.0);
      this._tensionDecelerating = true;
      this._tensionDecelElapsed = 0;
      this._tensionSpeedMultiplier = 1.0;
    }
  }

  /**
   * Stop tension mode spin — triggers normal stop.
   * Keeps isTensionMode=true so _finalizeLanding knows this was a tension stop.
   */
  stopSpinTension() {
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;
    this.isStopping = true;
    this.isTensionMode = true;
  }

  /**
   * Reset tension deceleration state (called on cleanup / new spin).
   */
  _resetTensionState() {
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionDecelerationDuration = 0;
    this._tensionSpeedMultiplier = 1.0;
    this.isTensionMode = false;
  }

  // ── Pause/Resume for tab switching — NO-OPS (matches ReelControllerV2) ──
  // AppActivityContext sets PIXI.Ticker.shared.speed = 0 on tab hide,
  // which makes deltaMS = 0 for all subscribers. Spin/stop/wind-up and
  // TickerTimeouts freeze automatically; no wall-clock compensation needed.
  pauseSpin() {}
  resumeSpin() {}

  // ── Freeze Spin (from ReelControllerV2) ────────────────────────────────
  // Instantly stops all animations without landing. Used for error popups
  // where cells should freeze at current position.

  freezeSpin() {
    // Stop ticker immediately
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Stop wind-up if active
    if (this._windUpTickerFn) {
      PIXI.Ticker.shared.remove(this._windUpTickerFn);
      this._windUpTickerFn = null;
      this.isWindingUp = false;
    }

    // Clear ticker-based stop animation params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this._landingPeakFired = false;

    // Set state flags
    this.isPlaying = false;
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.isTensionMode = false;
    this.spinStartTime = null;
    this._spinElapsed = 0;
    this.currentVelocity = 0;
    this.accelerationMultiplier = 1.0;

    // Reset tension state
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // DO NOT hide spinningContainer — keep sprites visible at their frozen positions.
    // Unlike _finalizeLanding() where the static sprite takes over, during a freeze
    // the static sprite is already hidden (set to alpha=0.001 at spin start).
    // Hiding spinning container too would make the cell completely invisible.
    // Matches ReelControllerV2.freezeSpin() which never touches container visibility.
  }

  // ── Cleanup ───────────────────────────────────────────────────────────

  cleanup() {
    if (this._windUpTickerFn) {
      PIXI.Ticker.shared.remove(this._windUpTickerFn);
      this._windUpTickerFn = null;
      this.isWindingUp = false;
    }
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (spriteData.container.parent) {
        spriteData.container.parent.removeChild(spriteData.container);
      }
      spriteData.container.destroy({ children: true });
    }
    this.spinningSprites = [];
    this._nativeDimensionsCache.clear();
  }
}

export default PotCollectionSymbolController;
